

import React, { useState } from 'react';
import type { AIDiagnosis, AIDiagnosisCause, VehicleInfo } from '../types';
import { aiService } from '../services/aiServiceFactory';

interface DiagnosisReportProps {
    report: AIDiagnosis | null;
    onReset: () => void;
    onSelectCause: (cause: AIDiagnosisCause) => void;
    vehicleInfo: VehicleInfo;
}

export const DiagnosisReport: React.FC<DiagnosisReportProps> = ({ report, onReset, onSelectCause, vehicleInfo }) => {
    const [expandedCauseIndex, setExpandedCauseIndex] = useState<number | null>(null);
    const [causeDetails, setCauseDetails] = useState<string | null>(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);

    const handleLearnMore = async (cause: AIDiagnosisCause, index: number) => {
        if (expandedCauseIndex === index) {
            setExpandedCauseIndex(null); // Collapse if already open
            return;
        }

        setExpandedCauseIndex(index);
        setIsLoadingDetails(true);
        setCauseDetails(null);
        setDetailsError(null);

        try {
            const details = await aiService.getCauseDetails(vehicleInfo, cause);
            setCauseDetails(details);
        } catch (e) {
            console.error("Failed to get cause details:", e);
            setDetailsError("Could not load details. Please try again.");
        } finally {
            setIsLoadingDetails(false);
        }
    };

    if (!report || !Array.isArray(report.causes) || report.causes.length === 0) {
        return (
            <div className="p-8 text-center">
                <p className="text-gray-500 mb-4">No diagnosis could be determined. Please start over.</p>
                <button
                    onClick={onReset}
                    className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#002A5C] hover:bg-[#001F44] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    Start New Diagnosis
                </button>
            </div>
        );
    }

    const sortedCauses = [...report.causes].sort((a) => a.likelihood === 'Most Likely' ? -1 : 1);

    return (
        <div className="p-6 sm:p-8">
            <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-700">Diagnosis Complete</h2>
                <p className="text-sm text-gray-500 mt-1">Select a cause below to get step-by-step repair instructions.</p>
            </div>
            
            <div className="space-y-4">
                {sortedCauses.map((c, index) => {
                    const isMostLikely = c.likelihood === 'Most Likely';
                    const isExpanded = expandedCauseIndex === index;

                    return (
                        <div key={index} className={`rounded-lg border transition-shadow duration-200 ${isMostLikely ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="p-4">
                                <div className="flex items-center mb-2">
                                    {isMostLikely ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                                            Most Likely
                                        </span>
                                     ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                                            Likely
                                        </span>
                                    )}
                                    <h4 className="font-semibold text-gray-800">{c.cause}</h4>
                                </div>
                                <p className="text-sm text-gray-600">{c.reason}</p>
                            </div>

                            <div className="px-4 pb-3 pt-2 border-t border-gray-200/80 flex justify-end items-center space-x-4 bg-white/50 rounded-b-lg">
                                <button
                                    onClick={() => handleLearnMore(c, index)}
                                    className="text-sm font-medium text-gray-600 hover:text-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 rounded-md px-2 py-1"
                                    aria-expanded={isExpanded}
                                    aria-controls={`cause-details-${index}`}
                                >
                                    {isExpanded ? 'Hide Details' : 'Learn More'}
                                </button>
                                <button
                                    onClick={() => onSelectCause(c)}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#002A5C] hover:bg-[#001F44] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    aria-label={`Get fix instructions for ${c.cause}`}
                                >
                                    Get Fix Steps
                                </button>
                            </div>

                            {isExpanded && (
                                <div id={`cause-details-${index}`} className="p-4 border-t border-gray-200/80 bg-white">
                                    {isLoadingDetails && (
                                        <div className="flex items-center justify-center py-4 space-x-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-t-2 border-[#002A5C]" />
                                            <p className="text-sm text-gray-500">Loading details...</p>
                                        </div>
                                    )}
                                    {detailsError && <p className="py-4 text-sm text-center text-red-600">{detailsError}</p>}
                                    {causeDetails && <p className="text-sm text-gray-600 whitespace-pre-wrap">{causeDetails}</p>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-8 text-center">
                 <button
                    onClick={onReset}
                    className="text-sm font-medium text-gray-600 hover:text-[#E31B23] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded-md px-3 py-1"
                >
                    Or, start a new diagnosis
                </button>
            </div>
        </div>
    );
};
